import { useTranslation } from "react-i18next";
import { useLanguage } from "../useLanguage";

interface AssistantData {
  language?: string;
}

interface UseCallTranslationsProps {
  assistantData?: AssistantData;
}

export const useCallTranslations = ({
  assistantData,
}: UseCallTranslationsProps) => {
  const { t, i18n } = useTranslation();

  // Usar idioma del assistant
  useLanguage({ assistantData });

  // Helper function para crear secciones de audioHelp
  const createAudioHelpSection = (sectionName: string) => ({
    title: t(`call:audioHelp.sections.${sectionName}.title`),
    items: [
      t(`call:audioHelp.sections.${sectionName}.items.0`),
      t(`call:audioHelp.sections.${sectionName}.items.1`),
      t(`call:audioHelp.sections.${sectionName}.items.2`),
    ],
  });

  const result = {
    t,
    translations: {
      greeting: (name: string) => t("call:greeting", { name }),
      connecting: t("call:connecting"),
      connectingSubtitle: t("call:connectingSubtitle"),
      mobileAlert: {
        title: t("call:mobileAlert.title"),
        message: t("call:mobileAlert.message"),
        instruction: t("call:mobileAlert.instruction"),
      },
      audioTest: {
        title: t("call:audioTest.title"),
        subtitle: t("call:audioTest.subtitle"),
        message: t("call:audioTest.message"),
        question: t("call:audioTest.question"),
        yesButton: t("call:audioTest.yesButton"),
        noButton: t("call:audioTest.noButton"),
        listenAgain: t("call:audioTest.listenAgain"),
      },
      audioHelp: {
        title: t("call:audioHelp.title"),
        subtitle: t("call:audioHelp.subtitle"),
        sections: {
          volume: {
            title: t("call:audioHelp.sections.volume.title"),
            items: [
              t("call:audioHelp.sections.volume.items.0"),
              t("call:audioHelp.sections.volume.items.1"),
              t("call:audioHelp.sections.volume.items.2"),
            ],
          },
          headphones: {
            title: t("call:audioHelp.sections.headphones.title"),
            items: [
              t("call:audioHelp.sections.headphones.items.0"),
              t("call:audioHelp.sections.headphones.items.1"),
              t("call:audioHelp.sections.headphones.items.2"),
            ],
          },
          connection: {
            title: t("call:audioHelp.sections.connection.title"),
            items: [
              t("call:audioHelp.sections.connection.items.0"),
              t("call:audioHelp.sections.connection.items.1"),
              t("call:audioHelp.sections.connection.items.2"),
            ],
          },
          browser: {
            title: t("call:audioHelp.sections.browser.title"),
            items: [
              t("call:audioHelp.sections.browser.items.0"),
              t("call:audioHelp.sections.browser.items.1"),
              t("call:audioHelp.sections.browser.items.2"),
            ],
          },
        },
        buttons: {
          goBack: t("call:audioHelp.buttons.goBack"),
          tryAgain: t("call:audioHelp.buttons.tryAgain"),
        },
      },
      status: {
        callInProgress: t("call:status.callInProgress"),
        callFinishedAcademy: t("call:status.callFinishedAcademy"),
        callFinished: t("call:status.callFinished"),
        assistantSpeaking: t("call:status.assistantSpeaking"),
        listening: t("call:status.listening"),
        readyToCall: t("call:status.readyToCall"),
        inCall: t("call:status.inCall"),
        disconnected: t("call:status.disconnected"),
      },
      buttons: {
        startCall: t("call:buttons.startCall"),
        endCall: t("call:buttons.endCall"),
        testSpeakers: t("call:buttons.testSpeakers"),
        startInterview: t("call:buttons.startInterview"),
      },
      microphone: {
        detected: t("call:microphone.detected"),
        noAccess: t("call:microphone.noAccess"),
        verifying: t("call:microphone.verifying"),
        speakNow: t("call:microphone.speakNow"),
        perfect: t("call:microphone.perfect"),
        noAccessError: t("call:microphone.noAccessError"),
      },
      deviceSelectors: {
        microphone: t("call:deviceSelectors.microphone"),
        speaker: t("call:deviceSelectors.speaker"),
      },
      playing: t("call:playing"),
      preparation: {
        title: t("call:preparation.title"),
        requirements: {
          connection: {
            title: t("call:preparation.requirements.connection.title"),
            description: t(
              "call:preparation.requirements.connection.description"
            ),
          },
          headphones: {
            title: t("call:preparation.requirements.headphones.title"),
            description: t(
              "call:preparation.requirements.headphones.description"
            ),
          },
          quietPlace: {
            title: t("call:preparation.requirements.quietPlace.title"),
            description: t(
              "call:preparation.requirements.quietPlace.description"
            ),
          },
          timeAvailable: {
            title: t("call:preparation.requirements.timeAvailable.title"),
            description: t(
              "call:preparation.requirements.timeAvailable.description"
            ),
          },
        },
      },
      npsModal: {
        title: t("call:npsModal.title"),
        question: t("call:npsModal.question"),
        subtitle: t("call:npsModal.subtitle"),
        labels: {
          veryBad: t("call:npsModal.labels.veryBad"),
          excellent: t("call:npsModal.labels.excellent"),
        },
        placeholder: t("call:npsModal.placeholder"),
        buttons: {
          skip: t("call:npsModal.buttons.skip"),
          sending: t("call:npsModal.buttons.sending"),
          submit: t("call:npsModal.buttons.submit"),
        },
      },
      microphonePermission: {
        title: t("call:microphonePermission.title"),
        subtitle: t("call:microphonePermission.subtitle"),
        instructions: {
          step1: t("call:microphonePermission.instructions.step1"),
          step2: t("call:microphonePermission.instructions.step2"),
          step3: t("call:microphonePermission.instructions.step3"),
        },
        blockedInstructions: {
          step1: t("call:microphonePermission.blockedInstructions.step1"),
          step2: t("call:microphonePermission.blockedInstructions.step2"),
          step3: t("call:microphonePermission.blockedInstructions.step3"),
          step4: t("call:microphonePermission.blockedInstructions.step4"),
          step5: t("call:microphonePermission.blockedInstructions.step5"),
          step6: t("call:microphonePermission.blockedInstructions.step6"),
        },
        buttons: {
          requestPermission: t("call:microphonePermission.buttons.requestPermission"),
          tryAgain: t("call:microphonePermission.buttons.tryAgain"),
          reloadPage: t("call:microphonePermission.buttons.reloadPage"),
        },
        errorBlocked: t("call:microphonePermission.errorBlocked"),
      },
    },
    audioHelp: {
      title: t("call:audioHelp.title"),
      subtitle: t("call:audioHelp.subtitle"),
      sections: {
        volume: createAudioHelpSection("volume"),
        headphones: createAudioHelpSection("headphones"),
        connection: createAudioHelpSection("connection"),
        browser: createAudioHelpSection("browser"),
      },
      buttons: {
        goBack: t("call:audioHelp.buttons.goBack"),
        tryAgain: t("call:audioHelp.buttons.tryAgain"),
      },
    },
  };

  return result;
};
